import { useRef, useState, type FormEvent } from 'react';

import { ApiError, api, setAccessToken } from '@/lib/api';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function ChangePasswordModal({ open, onClose, onSuccess }: Props) {
  // fechar clicando fora só se o clique começou e terminou no backdrop
  const downOnBackdrop = useRef(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  function reset() {
    setCurrent('');
    setNext('');
    setConfirm('');
    setError(null);
    setBusy(false);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (next !== confirm) {
      setError('a confirmação não bate com a nova senha.');
      return;
    }
    setBusy(true);
    try {
      await api<void>('/api/v1/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({ current_password: current, new_password: next }),
      });
      // backend revoga todos refresh tokens — precisamos re-logar
      setAccessToken(null);
      reset();
      onSuccess();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'invalid_credentials') setError('senha atual incorreta.');
        else if (err.code === 'conflict') setError('a nova senha precisa ser diferente da atual.');
        else if (err.code === 'validation_error')
          setError('nova senha precisa de 12+ chars, com letra e dígito.');
        else if (err.code === 'rate_limited') setError('muitas tentativas — aguarde.');
        else setError(err.message);
      } else {
        setError('falha ao trocar senha.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
      onMouseDown={(e) => {
        downOnBackdrop.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (downOnBackdrop.current && e.target === e.currentTarget) onClose();
        downOnBackdrop.current = false;
      }}
    >
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-xl">
        <h2 className="mb-4 text-lg font-semibold">Trocar senha</h2>
        <form onSubmit={submit} className="flex flex-col gap-3 text-sm">
          <label className="flex flex-col gap-1">
            <span className="text-slate-600">Senha atual</span>
            <input
              type="password"
              autoComplete="current-password"
              required
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="rounded-md border border-slate-300 px-3 py-2 outline-none focus:border-slate-900"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-slate-600">Nova senha (mín. 12, letra + dígito)</span>
            <input
              type="password"
              autoComplete="new-password"
              required
              minLength={12}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className="rounded-md border border-slate-300 px-3 py-2 outline-none focus:border-slate-900"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-slate-600">Confirmar nova senha</span>
            <input
              type="password"
              autoComplete="new-password"
              required
              minLength={12}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="rounded-md border border-slate-300 px-3 py-2 outline-none focus:border-slate-900"
            />
          </label>
          {error && (
            <p role="alert" className="text-red-600">
              {error}
            </p>
          )}
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                reset();
                onClose();
              }}
              className="rounded-md border border-slate-300 px-3 py-2 hover:bg-slate-100"
            >
              cancelar
            </button>
            <button
              type="submit"
              disabled={busy}
              className="rounded-md bg-slate-900 px-3 py-2 font-medium text-white disabled:opacity-50"
            >
              {busy ? 'trocando…' : 'trocar senha'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
